import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import type { AuditLogEntry, Json } from '@/types/database';

// =========================================
// Types
// =========================================

type AuditEntityType =
  | 'source'
  | 'folder'
  | 'criteria'
  | 'evaluation'
  | 'review'
  | 'team'
  | 'chat'
  | 'extraction'
  | 'report'
  | 'alert'
  | 'profile'
  | 'credits';

type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'upload'
  | 'evaluate'
  | 'import'
  | 'export'
  | 'invite'
  | 'login'
  | 'logout'
  | 'purchase'
  | 'chat'
  | 'search'
  | 'resolve';

interface LogAuditParams {
  action: AuditAction;
  entityType: AuditEntityType;
  entityId?: string;
  details?: Record<string, Json>;
}

// =========================================
// Query: fetch audit log entries
// =========================================

export function useAuditLog(limit: number = 30) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['audit-log', user?.id, limit],
    queryFn: async (): Promise<AuditLogEntry[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('audit_log')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as AuditLogEntry[];
    },
    enabled: !!user,
  });
}

// =========================================
// Mutation: log an audit event
// =========================================

export function useLogAudit() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ action, entityType, entityId, details }: LogAuditParams) => {
      if (!user) return;

      const { error } = await supabase.rpc('log_audit_event', {
        p_user_id: user.id,
        p_action: action,
        p_entity_type: entityType,
        p_entity_id: entityId ?? null,
        p_details: (details ?? {}) as Json,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-log'] });
    },
  });
}

// =========================================
// Helpers
// =========================================

const ACTION_LABELS: Record<AuditAction, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  upload: 'Uploaded',
  evaluate: 'Evaluated',
  import: 'Imported',
  export: 'Exported',
  invite: 'Invited',
  login: 'Logged in',
  logout: 'Logged out',
  purchase: 'Purchased',
  chat: 'Chat message',
  search: 'Searched',
  resolve: 'Resolved',
};

const ENTITY_LABELS: Record<AuditEntityType, string> = {
  source: 'source',
  folder: 'folder',
  criteria: 'criterion',
  evaluation: 'evaluation',
  review: 'review',
  team: 'team',
  chat: 'chat',
  extraction: 'extraction',
  report: 'report',
  alert: 'alert',
  profile: 'profile',
  credits: 'credits',
};

export function formatAuditAction(action: string): string {
  return ACTION_LABELS[action as AuditAction] ?? action;
}

export function formatAuditEntity(entityType: string): string {
  return ENTITY_LABELS[entityType as AuditEntityType] ?? entityType;
}
